/**
 * 日期展示工具
 *
 * 纯函数、零依赖（不引入 Vue / store / element-ui），可被单测直接 import。
 * 之所以独立于 util.js：util.js 顶部 import 了 store、element-ui、config，
 * 单测里直接引它会把整条依赖链拖进来；而 Jest 的 setup.js 又把 $util mock 成了
 * 只有 cloneObj 的对象，组件里写 this.$util.xxx 会直接抛错。
 *
 * 统一约定：
 * - 入参是后端返回的时间字符串，可能是 'yyyy-MM-dd HH:mm:ss'（VARCHAR 字段）
 *   也可能是 'yyyy-MM-ddTHH:mm:ss'（LocalDateTime 默认 ISO 序列化结果）
 * - 输出只用于展示，空值一律返回 ''，由调用方决定要不要兜底成 '—'
 */

const DAY_MS = 24 * 60 * 60 * 1000

/** 统一输入：去掉 ISO 的 T 分隔符 */
function normalize(time) {
    if (!time) return ''
    return String(time).replace('T', ' ').trim()
}

/**
 * 解析为 Date；用 / 分隔日期部分，避免 Safari 解析 'yyyy-MM-dd HH:mm:ss' 失败
 * @returns {Date|null} 解析失败返回 null
 */
function toDateTime(time) {
    const s = normalize(time)
    if (!s) return null
    const d = new Date(s.replace(/-/g, '/'))
    return isNaN(d.getTime()) ? null : d
}

/** 统一入参为 Date：支持 Date / 时间戳 / 时间字符串，解析失败返回 null */
function toDateObj(input) {
    if (input === null || input === undefined || input === '') return null
    if (input instanceof Date) return isNaN(input.getTime()) ? null : input
    if (typeof input === 'number') {
        const n = new Date(input)
        return isNaN(n.getTime()) ? null : n
    }
    return toDateTime(input)
}

/** 取日历日 0 点的时间戳（按本地时区，避免 UTC 偏移导致跨日） */
function startOfDay(time) {
    const d = toDateObj(time)
    if (!d) return null
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/** 相隔日历天数 = b - a；任一端无法解析时返回 null */
function dayDiff(a, b) {
    const x = startOfDay(a)
    const y = startOfDay(b)
    if (x === null || y === null) return null
    return Math.round((y - x) / DAY_MS)
}

/**
 * 格式化为 'YYYY-MM-DD HH:mm'
 * 沿用收费项目列表原有的截断语义（非 Date 解析），空值返回 ''
 */
export function formatYmdhm(time) {
    const s = normalize(time)
    if (!s) return ''
    return s.length > 16 ? s.substring(0, 16) : s
}

/**
 * 有效期区间文案，同年折叠右侧年份
 * 例：2026-09-01 ~ 09-30 / 2026-12-01 ~ 2027-01-31
 */
export function formatDateRange(begin, end) {
    const b = normalize(begin).substring(0, 10)
    const e = normalize(end).substring(0, 10)
    if (!b && !e) return ''
    if (b && !e) return b + ' 起'
    if (!b && e) return '至 ' + e
    if (b.substring(0, 4) === e.substring(0, 4)) return b + ' ~ ' + e.substring(5)
    return b + ' ~ ' + e
}

/**
 * 有效期状态
 *
 * 判定顺序：已过期 → 未开始 → 长期有效 → 进行中
 * 「已过期」用的是毫秒比较（与页面原有 isExpired 口径一致），
 * 天数差用日历日算，这样"今天结束"不会被渲染成"逾期 0 天"。
 *
 * @param {{beginTime?: string, endTime?: string}} row
 * @param {number|Date} [now] 当前时刻，缺省取 Date.now()；单测可注入
 * @returns {{key: string, label: string, tip: string, cls: string}}
 */
export function getValidityState(row, now) {
    const begin = row ? row.beginTime : ''
    const end = row ? row.endTime : ''
    const nowMs = now === undefined ? Date.now()
        : (now instanceof Date ? now.getTime() : Number(now))

    // 1) 已过期：结束时刻早于当前时刻
    if (end) {
        const endDt = toDateTime(end)
        if (endDt !== null && endDt.getTime() < nowMs) {
            const overdue = dayDiff(end, nowMs)
            return {
                key: 'expired',
                label: '已过期',
                tip: overdue > 0 ? '逾期 ' + overdue + ' 天' : '',
                cls: 'text-red'
            }
        }
    }

    // 2) 未开始：开始时刻晚于当前时刻
    if (begin) {
        const beginDt = toDateTime(begin)
        if (beginDt !== null && beginDt.getTime() > nowMs) {
            const after = dayDiff(nowMs, begin)
            return {
                key: 'notStarted',
                label: '未开始',
                tip: after > 0 ? after + ' 天后开始' : '今天开始',
                cls: 'text-gray'
            }
        }
    }

    // 3) 长期有效：没有结束时间
    if (!end) {
        return { key: 'forever', label: '长期有效', tip: '', cls: 'text-gray' }
    }

    // 4) 进行中
    const left = dayDiff(nowMs, end)
    return {
        key: 'ongoing',
        label: '进行中',
        tip: left > 0 ? '剩余 ' + left + ' 天' : '今天结束',
        cls: 'text-success'
    }
}

export default {
    formatYmdhm,
    formatDateRange,
    getValidityState
}



// WEBPACK FOOTER //
// ./src/assets/js/util/dateUtil.js